import { getSupabase } from './client';
import { requireUserId } from './auth';
import { apiRequest } from '../api';
import { t } from '../language';
import type { AccountPlan, PlanUsageDTO } from '../../shared/types';

/**
 * Hesabın planı ve içinde bulunduğumuz ayki kayıt kullanımı
 * (Ayarlar > Abonelik).
 *
 * Kullanım ve dönem sunucudan (`/api/v1/usage`): kotayı uygulayan aynı
 * hesap — canlı kayıt oturumlarının sunucu saatiyle süresi + bot süresi,
 * dönem ücretli planda ödeme gününe bağlı, ücretsizde İstanbul takvim ayı
 * (web/lib/billing/plan-rules.ts › usagePeriod). Silinen toplantılar dakikayı geri
 * vermez. Toplantı sayısı yalnızca bilgi amaçlı.
 */

interface PlanRow {
  plan: AccountPlan | null;
  monthly_minutes_quota: number | null;
  plan_until?: string | null;
  plan_source?: PlanUsageDTO['planSource'] | null;
}

interface ServerUsage {
  plan: AccountPlan;
  quotaMinutes: number;
  usedMinutes: number;
  periodStartISO: string;
  periodEndISO: string;
}

export async function getPlanUsage(): Promise<PlanUsageDTO> {
  const userId = await requireUserId();
  const supabase = getSupabase();

  const usage = await apiRequest<{ usage: ServerUsage }>('GET', '/api/v1/usage', { timeoutMs: 15_000 })
    .then((res) => res.usage)
    .catch((err) => {
      console.error('[plan] kullanım okunamadı:', (err as Error).message);
      throw new Error(t('Kullanım bilgisi yüklenemedi.', 'Could not load your usage.'));
    });

  // plan_until/plan_source migration 0024 ile geldi; henüz uygulanmamış veritabanında eski sorguya düş.
  const readProfile = async () => {
    const result = await supabase.from('profiles').select('plan, monthly_minutes_quota, plan_until, plan_source').eq('id', userId).maybeSingle();
    if (result.error?.code !== '42703') return result;
    return supabase.from('profiles').select('plan, monthly_minutes_quota').eq('id', userId).maybeSingle();
  };
  const [profile, meetings] = await Promise.all([
    readProfile(),
    supabase
      .from('meetings')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('deleted_at', null)
      .gte('recorded_at', usage.periodStartISO)
      .lt('recorded_at', usage.periodEndISO),
  ]);

  if (profile.error) {
    console.error('[plan] profil okunamadı:', profile.error.message);
    throw new Error(t('Plan bilgisi yüklenemedi.', 'Could not load your plan.'));
  }

  const row = (profile.data ?? null) as PlanRow | null;
  const plan = usage.plan ?? row?.plan ?? 'basic';
  return {
    plan,
    planUntilISO: row?.plan_until ?? undefined,
    planSource: row?.plan_source ?? undefined,
    quotaMinutes: Number(usage.quotaMinutes ?? row?.monthly_minutes_quota ?? 0),
    usedMinutes: Number(usage.usedMinutes ?? 0),
    meetingCount: meetings.count ?? 0,
    periodStartISO: usage.periodStartISO,
    periodEndISO: usage.periodEndISO,
  };
}
